import Dexie from 'dexie';
import { db, SYNC_STATUS, LOCAL_WORKSPACE_TENANT_ID } from '../lib/database';
import { outboxManager, OUTBOX_ACTIONS } from '../lib/sync/OutboxManager';
import { generateRemoteId } from '../lib/sync/ids';
import { getFormConfig } from '../forms';
import { generateSerialNumber } from '../utils/formatters';

// ---------------------------------------------------------------------------
// Customer & Property Asset CRM — service layer (Track 5)
//
// Every read is tenant-scoped via compound indexes ([tenantId+...]); every
// write stamps `tenantId`, `syncStatus`, `mutationVersion` and enqueues a
// delta into the outbox so the change replicates to PocketBase when back
// online. A write that skips the outbox would never reach the cloud.
// ---------------------------------------------------------------------------

const identity = () => ({
  tenantId: dbUtilsActiveTenant(),
  userId: 'user_local'
});

/** Resolve the active tenant id (local-scope fallback keeps offline working). */
function dbUtilsActiveTenant() {
  try {
    const tenantId = localStorage.getItem('saas_active_tenant_id');
    return tenantId || LOCAL_WORKSPACE_TENANT_ID;
  } catch {
    return LOCAL_WORKSPACE_TENANT_ID;
  }
}

/** Minimal uuid v4 (crypto.randomUUID fallback) for outbox idempotency keys. */
function uuid() {
  try {
    return crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  } catch {
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }
}

const baseSyncFields = (tenantId) => ({
  tenantId,
  syncStatus: SYNC_STATUS.PENDING,
  mutationVersion: 1
});

export class CustomerService {
  // -------------------------------------------------------------------------
  // Customers (Track 5.1)
  // -------------------------------------------------------------------------

  /**
   * Create a customer record (tenant-scoped + outbox CREATE).
   * @param {Object} payload { name, type, email, phone, emails[], billingAddress, notes }
   * @returns {Promise<Object>} saved record
   */
  static async createCustomer(payload = {}) {
    await db.ensureReady();
    const { tenantId } = identity();
    const now = new Date();
    const remoteId = generateRemoteId(); // PB-compatible cross-device identity

    const record = {
      ...baseSyncFields(tenantId),
      name: (payload.name || '').trim(),
      type: payload.type || 'homeowner',
      email: (payload.email || '').trim(),
      phone: (payload.phone || '').trim(),
      emails: Array.isArray(payload.emails) && payload.emails.length
        ? payload.emails.filter(Boolean).map((e) => e.trim())
        : [],
      billingAddress: payload.billingAddress || '',
      notes: payload.notes || '',
      createdAt: now,
      updatedAt: now,
      remoteId
    };

    record.id = await db.customers.add(record);

    // Replicate to cloud when online.
    await outboxManager.enqueue({
      deltaId: uuid(),
      collection: 'customers',
      recordId: record.id,
      action: OUTBOX_ACTIONS.CREATE,
      payload: record,
      identity: { tenantId }
    });

    return record;
  }

  /**
   * Update an existing customer (tenant-scoped + outbox UPDATE).
   */
  static async updateCustomer(customerId, patch = {}) {
    await db.ensureReady();
    const existing = await db.customers.get(Number(customerId));
    if (!existing) throw new Error('Customer not found');

    const { tenantId } = identity();
    // Never allow a client to move a record across tenancy.
    if (existing.tenantId !== tenantId) {
      throw new Error(`Customer ${customerId} does not belong to the active tenant`);
    }

    const updated = {
      ...existing,
      ...patch,
      name: (patch.name ?? existing.name ?? '').trim(),
      emails: Array.isArray(patch.emails)
        ? patch.emails.filter(Boolean).map((e) => e.trim())
        : existing.emails,
      updatedAt: new Date(),
      syncStatus: SYNC_STATUS.PENDING,
      mutationVersion: (existing.mutationVersion || 0) + 1
    };

    await db.customers.put(updated);

    await outboxManager.enqueue({
      deltaId: uuid(),
      collection: 'customers',
      recordId: updated.id,
      action: OUTBOX_ACTIONS.UPDATE,
      payload: updated,
      identity: { tenantId }
    });

    return updated;
  }

  /**
   * Soft-delete a customer (sets deletedAt; outbox DELETE).
   */
  static async deleteCustomer(customerId) {
    await db.ensureReady();
    const existing = await db.customers.get(Number(customerId));
    if (!existing) throw new Error('Customer not found');
    const { tenantId } = identity();
    if (existing.tenantId !== tenantId) {
      throw new Error(`Customer ${customerId} does not belong to the active tenant`);
    }

    const softDeleted = { ...existing, deletedAt: new Date(), updatedAt: new Date(), syncStatus: SYNC_STATUS.PENDING };
    await db.customers.put(softDeleted);

    await outboxManager.enqueue({
      deltaId: uuid(),
      collection: 'customers',
      recordId: existing.id,
      action: OUTBOX_ACTIONS.DELETE,
      payload: { id: existing.id, deletedAt: softDeleted.deletedAt },
      identity: { tenantId }
    });

    return existing.id;
  }

  /**
   * All customers for the active tenant, newest first (excluding soft-deleted).
   * @param {string} [tenantId]
   */
  static async getCustomers(tenantId = null) {
    await db.ensureReady();
    const tid = tenantId || dbUtilsActiveTenant();

    const customers = await db.customers
      .where('[tenantId+createdAt]')
      .between([tid, Dexie.minKey], [tid, Dexie.maxKey])
      .reverse()
      .toArray();

    return customers.filter((c) => !c.deletedAt);
  }

  static async getCustomer(customerId) {
    await db.ensureReady();
    return await db.customers.get(Number(customerId));
  }

  /**
   * Fast text search over name / email / phone (tenant-scoped full scan,
   * cheape than multiple index ORs for small local CRM sets).
   */
  static async searchCustomers(searchTerm, tenantId = null) {
    await db.ensureReady();
    const tid = tenantId || dbUtilsActiveTenant();
    const q = (searchTerm || '').toLowerCase().trim();
    if (!q) return this.getCustomers(tid);

    const all = await db.customers
      .where('[tenantId+createdAt]')
      .between([tid, Dexie.minKey], [tid, Dexie.maxKey])
      .toArray();

    return all
      .filter((c) => !c.deletedAt)
      .filter((c) => {
        const haystack = [
          c.name,
          c.email,
          c.phone,
          ...(c.emails || []),
          c.billingAddress,
          c.notes
        ].filter(Boolean).join(' ').toLowerCase();
        return haystack.includes(q);
      });
  }

  // -------------------------------------------------------------------------
  // Properties / Site asset registry (Track 5.2)
  // -------------------------------------------------------------------------

  /** Create a property linked to a customer (tenant-scoped + outbox CREATE). */
  static async createProperty(payload = {}) {
    await db.ensureReady();
    const { tenantId } = identity();
    const now = new Date();
    const remoteId = generateRemoteId(); // PB-compatible cross-device identity

    const record = {
      ...baseSyncFields(tenantId),
      remoteId,
      customerId: payload.customerId ? Number(payload.customerId) : null,
      type: payload.type || 'residential',
      addressLine1: (payload.addressLine1 || '').trim(),
      addressLine2: (payload.addressLine2 || '').trim(),
      city: (payload.city || '').trim(),
      postcode: (payload.postcode || '').trim(),
      country: (payload.country || 'UK').trim(),
      appliances: Array.isArray(payload.appliances) ? payload.appliances : [],
      notes: payload.notes || '',
      createdAt: now,
      updatedAt: now
    };

    record.id = await db.properties.add(record);

    await outboxManager.enqueue({
      deltaId: uuid(),
      collection: 'properties',
      recordId: record.id,
      action: OUTBOX_ACTIONS.CREATE,
      payload: record,
      identity: { tenantId }
    });

    return record;
  }

  /** Update a property (tenant check + outbox UPDATE). */
  static async updateProperty(propertyId, patch = {}) {
    await db.ensureReady();
    const existing = await db.properties.get(Number(propertyId));
    if (!existing) throw new Error('Property not found');
    const { tenantId } = identity();
    if (existing.tenantId !== tenantId) {
      throw new Error(`Property ${propertyId} does not belong to the active tenant`);
    }

    const updated = {
      ...existing,
      ...patch,
      customerId: patch.customerId ? Number(patch.customerId) : existing.customerId ?? null,
      appliances: Array.isArray(patch.appliances) ? patch.appliances : existing.appliances,
      updatedAt: new Date(),
      syncStatus: SYNC_STATUS.PENDING,
      mutationVersion: (existing.mutationVersion || 0) + 1
    };

    await db.properties.put(updated);

    await outboxManager.enqueue({
      deltaId: uuid(),
      collection: 'properties',
      recordId: updated.id,
      action: OUTBOX_ACTIONS.UPDATE,
      payload: updated,
      identity: { tenantId }
    });

    return updated;
  }

  /** Soft-delete a property (+ outbox DELETE). */
  static async deleteProperty(propertyId) {
    await db.ensureReady();
    const existing = await db.properties.get(Number(propertyId));
    if (!existing) throw new Error('Property not found');
    const { tenantId } = identity();
    if (existing.tenantId !== tenantId) {
      throw new Error(`Property ${propertyId} does not belong to the active tenant`);
    }

    const softDeleted = { ...existing, deletedAt: new Date(), updatedAt: new Date(), syncStatus: SYNC_STATUS.PENDING };
    await db.properties.put(softDeleted);

    await outboxManager.enqueue({
      deltaId: uuid(),
      collection: 'properties',
      recordId: existing.id,
      action: OUTBOX_ACTIONS.DELETE,
      payload: { id: existing.id, deletedAt: softDeleted.deletedAt },
      identity: { tenantId }
    });

    return existing.id;
  }

  /** All properties for the active tenant (excluding soft-deleted). */
  static async getProperties(tenantId = null) {
    await db.ensureReady();
    const tid = tenantId || dbUtilsActiveTenant();

    const props = await db.properties
      .where('[tenantId+createdAt]')
      .between([tid, Dexie.minKey], [tid, Dexie.maxKey])
      .reverse()
      .toArray();

    return props.filter((p) => !p.deletedAt);
  }

  /** Properties linked to one customer. */
  static async getPropertiesByCustomer(customerId, tenantId = null) {
    await db.ensureReady();
    const tid = tenantId || dbUtilsActiveTenant();
    const props = await db.properties
      .where('[tenantId+customerId]')
      .equals([tid, Number(customerId)])
      .toArray();
    return props.filter((p) => !p.deletedAt);
  }

  static async getProperty(propertyId) {
    await db.ensureReady();
    return await db.properties.get(Number(propertyId));
  }

  /** Search properties by address/postcode (tenant-scoped). */
  static async searchProperties(searchTerm, tenantId = null) {
    await db.ensureReady();
    const tid = tenantId || dbUtilsActiveTenant();
    const q = (searchTerm || '').toLowerCase().trim();
    if (!q) return this.getProperties(tid);

    const all = await db.properties
      .where('[tenantId+createdAt]')
      .between([tid, Dexie.minKey], [tid, Dexie.maxKey])
      .toArray();

    return all.filter((p) => !p.deletedAt).filter((p) => {
      const haystack = [
        p.addressLine1,
        p.addressLine2,
        p.city,
        p.postcode,
        p.country,
        p.type,
        (p.appliances || []).map((a) => `${a.make} ${a.model} ${a.type}`).join(' ')
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }

  /** Update the appliance registry of a property. */
  static async updatePropertyAppliances(propertyId, appliances) {
    return this.updateProperty(propertyId, { appliances: Array.isArray(appliances) ? appliances : [] });
  }

  // -------------------------------------------------------------------------
  // Certificate history & prefill (Track 5.1 / 5.2)
  // -------------------------------------------------------------------------

  /**
   * Certificate history for a customer (all forms linked to them).
   */
  static async getCustomerCertificates(customerId, tenantId = null) {
    await db.ensureReady();
    const tid = tenantId || dbUtilsActiveTenant();
    const records = await db.forms
      .where('[tenantId+customerId]')
      .equals([tid, Number(customerId)])
      .toArray();
    return records
      .filter((f) => !f.deletedAt)
      .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
  }

  /**
   * Certificate history for a property.
   */
  static async getPropertyCertificates(propertyId, tenantId = null) {
    await db.ensureReady();
    const tid = tenantId || dbUtilsActiveTenant();
    const records = await db.forms
      .where('[tenantId+propertyId]')
      .equals([tid, Number(propertyId)])
      .toArray();
    return records
      .filter((f) => !f.deletedAt)
      .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
  }

  /**
   * Build a pre-filled form payload from a property + its customer.
   *
   * Maps the property's address / postcode / appliances and the connected
   * customer name/email/phone into the certificate form module's section
   * shapes. Field ids are per-form schema driven and interwoven below (the
   * common denominator across gas / electrical / oil / plumbing forms).
   *
   * @param {number} propertyId
   * @param {string} formId registry key (e.g. 'gas-safety-record')
   * @returns {Promise<Object>} { data, links } pre-filled payload + relation metadata
   */
  static async buildPrefillData(propertyId, formId) {
    await db.ensureReady();
    const { tenantId } = identity();

    const property = await this.getProperty(propertyId);
    if (!property) throw new Error('Property not found');
    if (property.tenantId !== tenantId) {
      throw new Error(`Property ${propertyId} does not belong to the active tenant`);
    }

    const customer = property.customerId
      ? await this.getCustomer(property.customerId)
      : null;

    const formConfig = getFormConfig(formId);
    const blankData = formConfig?.defaultData ? JSON.parse(JSON.stringify(formConfig.defaultData)) : {};

    const addressLine = [
      (property.addressLine1 || '').trim(),
      (property.addressLine2 || '').trim(),
      (property.city || '').trim()
    ].filter(Boolean).join(', ');

    /**
     * Merge helper: assign a value to a nested path only if the current
     * value is falsy (never clobber user-entered data). Path segments are
     * section / field keys from the form schema.
     */
    const assignIfEmpty = (target, path, value) => {
      if (!value && value !== 0) return;
      let node = target;
      for (let i = 0; i < path.length - 1; i += 1) {
        const key = path[i];
        if (node[key] === undefined || node[key] === null || typeof node[key] !== 'object') {
          node[key] = {};
        }
        node = node[key];
      }
      const last = path[path.length - 1];
      const empty = node[last] === undefined || node[last] === null || node[last] === '';
      if (empty) node[last] = value;
    };

    // --- Property address / postcode variations across module schemas ----
    // Assign address (line1, line2, city) to whichever schema field exists.
    const addressTargets = [
      ['inspectionAddress', 'address'], ['installation', 'address'],
      ['property', 'address'], ['site', 'address'],
      ['customer', 'address'], ['location', 'address'],
      ['address', 'address'], ['propertyAddress', 'address']
    ];
    for (const path of addressTargets) assignIfEmpty(blankData, path, addressLine);

    const postcodeTargets = [
      ['inspectionAddress', 'postCode'], ['installation', 'postCode'],
      ['property', 'postCode'], ['site', 'postCode'],
      ['customer', 'postCode'], ['location', 'postCode'],
      ['address', 'postCode']
    ];
    for (const path of postcodeTargets) assignIfEmpty(blankData, path, property.postcode);

    // --- Customer details -------------------------------------------------
    if (customer) {
      const nameTargets = [
        ['landlord', 'name'], ['landlord', 'customerName'],
        ['property', 'landlord'], ['property', 'customerName'],
        ['customer', 'name'], ['inspectionAddress', 'tenantName'],
        ['his', 'customerName']
      ];
      for (const path of nameTargets) assignIfEmpty(blankData, path, customer.name);

      const emailTargets = [
        ['landlord', 'email'], ['customer', 'email'],
        ['installer', 'email'], ['client', 'email']
      ];
      for (const path of emailTargets) assignIfEmpty(blankData, path, customer.email);

      const phoneTargets = [
        ['landlord', 'tel'], ['landlord', 'phone'],
        ['customer', 'phone'], ['customer', 'tel'],
        ['inspectionAddress', 'tel'],
        ['installer', 'tel']
      ];
      for (const path of phoneTargets) assignIfEmpty(blankData, path, customer.phone);

      // Billing address is only meaningful for landlords/agents.
      if (customer.billingAddress) {
        const billTargets = [
          ['landlord', 'address'], ['customer', 'billingAddress'],
          ['billing', 'address'], ['landlord', 'billingAddress']
        ];
        for (const path of billTargets) assignIfEmpty(blankData, path, customer.billingAddress);
      }
    }

    // --- Appliance registry -> repeatable appliance sections -------------
    // Registered appliances (make/model/flueType) pre-fill the standard
    // repeatable `appliances` sections found in gas-safety modules.
    const appliances = (property.appliances || []).filter((a) => a && (a.make || a.model || a.type));
    if (appliances.length > 0 && Array.isArray(blankData.appliances)) {
      blankData.appliances = appliances.map((a) => ({
        location: a.location || '',
        type: a.type || '',
        make: a.make || '',
        model: a.model || '',
        flueType: a.flueType || '',
        ...(blankData.appliances[0] ? blankData.appliances[0] : {})
      }));
    }

    return {
      data: blankData,
      links: {
        propertyId: property.id,
        customerId: customer ? customer.id : null,
        tenantId
      }
    };
  }

  /**
   * Persist a pre-filled certificate draft linked to the property/customer.
   * Returns the saved record so the caller can route to the form editor.
   */
  static async createPrefilledForm(formId, propertyId) {
    const { data, links } = await this.buildPrefillData(propertyId, formId);
    const { tenantId } = identity();
    const now = new Date();

    const record = {
      formId,
      instanceId: `${formId}-${generateSerialNumber('CRT')}`,
      serialNumber: generateSerialNumber('SN'),
      data,
      status: 'draft',
      tenantId,
      userId: 'user_local',
      syncStatus: SYNC_STATUS.PENDING,
      mutationVersion: 1,
      customerId: links.customerId || null,
      propertyId: links.propertyId || null,
      createdAt: now,
      updatedAt: now,
      lastAccessedAt: now
    };

    record.id = await db.forms.add(record);

    await outboxManager.enqueue({
      deltaId: uuid(),
      collection: 'forms',
      recordId: record.id,
      action: OUTBOX_ACTIONS.CREATE,
      payload: record,
      identity: { tenantId }
    });

    return record;
  }
}

/** Aggregate stats for the customer directory header chips. */
export const getCRMStats = async (tenantId = null) => {
  await db.ensureReady();
  const tid = tenantId || dbUtilsActiveTenant();

  const [customers, properties, forms] = await Promise.all([
    db.customers.where('[tenantId+createdAt]').between([tid, Dexie.minKey], [tid, Dexie.maxKey]).toArray(),
    db.properties.where('[tenantId+createdAt]').between([tid, Dexie.minKey], [tid, Dexie.maxKey]).toArray(),
    db.forms.where('[tenantId+createdAt]').between([tid, Dexie.minKey], [tid, Dexie.maxKey]).toArray()
  ]);

  const activeCustomers = customers.filter((c) => !c.deletedAt);
  const activeProperties = properties.filter((p) => !p.deletedAt);
  const activeForms = forms.filter((f) => !f.deletedAt);

  return {
    customerCount: activeCustomers.length,
    propertyCount: activeProperties.length,
    certificateCount: activeForms.length,
    issuedCertificates: activeForms.filter((f) => f.status === 'issued' || f.status === 'completed').length,
    appliances: activeProperties.reduce((total, p) => total + (p.appliances?.length || 0), 0)
  };
};